import type {
  HelmCorner,
  HelmCorridor,
  HelmEdge,
  HelmHole
} from '@features/fields/api/useHelmInspect'
import * as turf from '@turf/turf'
import type { Position } from 'geojson'

// About a centimeter: helm round-trips coordinates through a local
// projection, so the same input comes back with floating-point noise.
const COORDINATE_SCALE = 1e7

const pointKey = ([lng, lat]: Position) =>
  `${Math.round(lng * COORDINATE_SCALE)}_${Math.round(lat * COORDINATE_SCALE)}`

const ringKey = (positions: Position[]) =>
  positions.map(pointKey).reduce((min, key) => (key < min ? key : min))

export const edgeKey = ({ region, feature }: Pick<HelmEdge, 'region' | 'feature'>) => {
  const positions = feature.geometry.coordinates
  const first = pointKey(positions[0])
  const last = pointKey(positions[positions.length - 1])
  if (first === last) return `${region}|loop|${ringKey(positions)}`
  const [a, b] = [first, last].sort()
  const midway = turf.along(feature, turf.length(feature) / 2).geometry.coordinates
  return `${region}|side|${a}~${b}|${pointKey(midway)}`
}

export const cornerKey = ({ region, feature }: Pick<HelmCorner, 'region' | 'feature'>) =>
  `${region}|corner|${pointKey(feature.geometry.coordinates)}`

export const holeKey = ({ region, feature }: Pick<HelmHole, 'region' | 'feature'>) =>
  `${region}|hole|${ringKey(feature.geometry.coordinates[0])}`

export const corridorKey = ({ regions, feature }: Pick<HelmCorridor, 'regions' | 'feature'>) =>
  `${[...regions].sort().join('~')}|corridor|${ringKey(feature.geometry.coordinates[0])}`

export const withOverride = <T extends { key: string }>(
  overrides: T[],
  key: string,
  entry: T | undefined
): T[] => [...overrides.filter((o) => o.key !== key), ...(entry === undefined ? [] : [entry])]
